/**
 * modeScreenTheme.js
 *
 * Premium palette overlay for the legacy-themed mode screens (Service /
 * repairman, Warehouse, Calendar).
 *
 * Those screens were written against the shared app theme (`config/themes`), whose
 * `colors.primary` is the legacy amber also used by the POS screens. POS is out of
 * the redesign scope, so the shared theme cannot be edited — instead the mode
 * screens swap `useTheme()` for {@link usePremiumLegacyTheme}, a drop-in that
 * returns the SAME shape with the colour tokens re-pointed at the premium
 * management palette (orange `#F97316`, airy `#F5F6FA` canvas, white cards,
 * navy surfaces in dark mode). Every `theme.colors.*` read in those screens — and
 * in the shared `ServiceUI` primitives that receive `theme` as a prop — therefore
 * picks up the new look without touching the screen's data flow or behaviour.
 *
 * @module helpers/modeScreenTheme
 */

import {useMemo} from 'react';
import {useTheme} from './ThemeContext';
import {BRAND, useManagementTheme} from './managementTheme';

/**
 * Re-point a legacy theme at the premium palette.
 *
 * @param {object} baseTheme Theme from `config/themes` (`{dark, colors}`).
 * @param {object} mc `useManagementTheme().colors` palette.
 * @returns {object} A new theme with the same keys, premium values.
 */
export const buildPremiumLegacyTheme = (baseTheme, mc) => {
    const base = baseTheme && typeof baseTheme === 'object' ? baseTheme : {dark: false, colors: {}};

    return {
        ...base,
        colors: {
            ...base.colors,
            primary: BRAND.base,
            background: mc.bg,
            card: mc.surface,
            surface: mc.surface,
            onSurface: mc.heading,
            text: mc.heading,
            subText: mc.subText,
            border: mc.border,
            inputBackground: mc.inputBg,
            placeholder: mc.placeholder,
            // The legacy "accent" blue marked in-progress work; the blueprint keeps
            // blue for the semantic info tone only, which is exactly that use.
            accent: mc.info,
            success: mc.success,
            warning: mc.warning,
            error: mc.danger,
        },
    };
};

/**
 * Drop-in replacement for `useTheme()` in the Service / Warehouse / Calendar
 * screens: identical return shape (`theme`, `isDark`, `themeMode`, …) with the
 * premium palette applied to `theme.colors`.
 *
 * @returns {object} The ThemeContext value with a premium `theme`.
 */
export const usePremiumLegacyTheme = () => {
    const context = useTheme();
    const m = useManagementTheme();
    const {theme} = context;

    const premiumTheme = useMemo(() => buildPremiumLegacyTheme(theme, m.colors), [theme, m.colors]);

    return useMemo(() => ({...context, theme: premiumTheme}), [context, premiumTheme]);
};

export default usePremiumLegacyTheme;
